/**
 * Budget Store
 * Zustand store for managing budget, transactions, and budget limits state.
 */

import create from 'zustand';
import { budgetApi } from '../api/endpoints/budget';
import type { ForecastData, RecurringTransaction } from '../api/endpoints/budget';
import type { Transaction, BudgetLimit, BudgetSummary } from '../api/types';
import logger from '../utils/logger';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export interface DateRange {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD
}

interface BudgetState {
  transactions: Transaction[];
  budgetLimits: BudgetLimit[];
  summary: BudgetSummary | null;
  dateRange: DateRange;
  isLoading: boolean;
  error: string | null;

  // Forecast
  forecast: ForecastData | null;
  forecastLoading: boolean;

  // Recurring
  recurringTransactions: RecurringTransaction[];
  recurringLoading: boolean;

  // Filter / Search / Pagination
  filterType: 'all' | 'income' | 'expense';
  filterTags: string[];
  searchQuery: string;
  showExcluded: boolean;
  page: number;
  perPage: number;
  totalCount: number;

  // Actions
  loadTransactions: () => Promise<void>;
  addTransaction: (data: {
    description: string;
    amount: number;
    category: string;
    transaction_type: 'income' | 'expense';
    transaction_date: string;
    notes?: string;
    excluded_from_budget?: boolean;
    tags?: string[];
  }) => Promise<void>;
  updateTransaction: (id: string, data: Partial<{
    description: string;
    amount: number;
    category: string;
    transaction_type: 'income' | 'expense';
    transaction_date: string;
    notes?: string;
    excluded_from_budget?: boolean;
    tags?: string[];
  }>) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;

  loadBudgetLimits: () => Promise<void>;
  addBudgetLimit: (data: {
    category?: string;
    tag?: string;
    monthly_limit: number;
    name?: string;
    icon?: string;
    color?: string;
  }) => Promise<void>;
  updateBudgetLimit: (id: string, data: {
    monthly_limit?: number;
    name?: string;
  }) => Promise<void>;
  deleteBudgetLimit: (id: string) => Promise<void>;

  loadSummary: () => Promise<void>;
  loadForecast: () => Promise<void>;
  loadRecurring: () => Promise<void>;
  setDateRange: (start: string, end: string) => void;
  /** Backward-compatible helper: sets the range to the given month bounds. */
  setMonth: (month: string) => void;
  setFilterType: (type: 'all' | 'income' | 'expense') => void;
  setFilterTags: (tags: string[]) => void;
  toggleFilterTag: (tag: string) => void;
  setSearchQuery: (query: string) => void;
  setShowExcluded: (show: boolean) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  refresh: () => Promise<void>;
}

const DEFAULT_PER_PAGE = 50;

const monthBounds = (): DateRange => {
  const now = new Date();
  return {
    start: format(startOfMonth(now), 'yyyy-MM-dd'),
    end: format(endOfMonth(now), 'yyyy-MM-dd'),
  };
};

const monthBoundsForString = (month: string): DateRange => {
  const date = new Date(`${month}-01T00:00:00`);
  return {
    start: format(startOfMonth(date), 'yyyy-MM-dd'),
    end: format(endOfMonth(date), 'yyyy-MM-dd'),
  };
};

export const useBudgetStore = create<BudgetState>((set, get) => ({
  transactions: [],
  budgetLimits: [],
  summary: null,
  dateRange: monthBounds(),
  isLoading: false,
  error: null,
  forecast: null,
  forecastLoading: false,
  recurringTransactions: [],
  recurringLoading: false,
  filterType: 'all',
  filterTags: [],
  searchQuery: '',
  showExcluded: false,
  page: 1,
  perPage: DEFAULT_PER_PAGE,
  totalCount: 0,

  loadTransactions: async () => {
    const { dateRange, filterType, filterTags, searchQuery, showExcluded, page, perPage } = get();
    try {
      set({ isLoading: true, error: null });
      const params: Record<string, any> = {
        from_date: dateRange.start,
        to_date: dateRange.end,
        page,
        per_page: perPage,
      };
      if (filterType !== 'all') params.transaction_type = filterType;
      if (filterTags.length > 0) params.tags = filterTags;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (showExcluded) params.include_excluded = true;

      const response = await budgetApi.getTransactions(params);
      set({
        transactions: response.transactions || [],
        totalCount: response.total_count,
      });
    } catch (err: any) {
      logger.error('[BudgetStore] Error loading transactions:', err);
      set({ error: 'Erreur lors du chargement des transactions' });
    } finally {
      set({ isLoading: false });
    }
  },

  addTransaction: async (data) => {
    try {
      set({ isLoading: true, error: null });
      const response = await budgetApi.createTransaction(data);
      set((state) => ({
        transactions: [response.transaction, ...state.transactions],
      }));
      get().loadSummary();
    } catch (err: any) {
      logger.error('[BudgetStore] Error adding transaction:', err);
      const message = err.response?.data?.error || 'Erreur lors de l\'ajout de la transaction';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateTransaction: async (id, data) => {
    try {
      set({ isLoading: true, error: null });
      const response = await budgetApi.updateTransaction(id, data);
      set((state) => ({
        transactions: state.transactions.map((tx) =>
          tx.id === id ? { ...tx, ...response.transaction } : tx
        ),
      }));
      get().loadSummary();
    } catch (err: any) {
      logger.error('[BudgetStore] Error updating transaction:', err);
      const message = err.response?.data?.error || 'Erreur lors de la mise à jour';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteTransaction: async (id) => {
    try {
      set({ isLoading: true, error: null });
      await budgetApi.deleteTransaction(id);
      set((state) => ({
        transactions: state.transactions.filter((tx) => tx.id !== id),
      }));
      get().loadSummary();
    } catch (err: any) {
      logger.error('[BudgetStore] Error deleting transaction:', err);
      const message = err.response?.data?.error || 'Erreur lors de la suppression';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  loadBudgetLimits: async () => {
    const { dateRange } = get();
    try {
      set({ error: null });
      // Backend currently scopes spending by month for budget limits.
      // Pass the start of the date range so the limit shows the right month's spending.
      const month = dateRange.start.slice(0, 7);
      const limits = await budgetApi.getBudgetLimits(month);
      set({ budgetLimits: limits });
    } catch (err: any) {
      logger.error('[BudgetStore] Error loading budget limits:', err);
      set({ error: 'Erreur lors du chargement des plafonds' });
    }
  },

  addBudgetLimit: async (data) => {
    try {
      set({ isLoading: true, error: null });
      const newLimit = await budgetApi.createBudgetLimit(data);
      set((state) => ({
        budgetLimits: [...state.budgetLimits, newLimit],
      }));
    } catch (err: any) {
      logger.error('[BudgetStore] Error adding budget limit:', err);
      const message = err.response?.data?.error || 'Erreur lors de l\'ajout du plafond';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateBudgetLimit: async (id, data) => {
    try {
      set({ isLoading: true, error: null });
      const updated = await budgetApi.updateBudgetLimit(id, data);
      set((state) => ({
        budgetLimits: state.budgetLimits.map((bl) => (bl.id === id ? updated : bl)),
      }));
    } catch (err: any) {
      logger.error('[BudgetStore] Error updating budget limit:', err);
      const message = err.response?.data?.error || 'Erreur lors de la mise à jour';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteBudgetLimit: async (id) => {
    try {
      set({ isLoading: true, error: null });
      await budgetApi.deleteBudgetLimit(id);
      set((state) => ({
        budgetLimits: state.budgetLimits.filter((bl) => bl.id !== id),
      }));
    } catch (err: any) {
      logger.error('[BudgetStore] Error deleting budget limit:', err);
      const message = err.response?.data?.error || 'Erreur lors de la suppression';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  loadSummary: async () => {
    const { dateRange } = get();
    try {
      set({ error: null });
      const response = await budgetApi.getBudgetSummary({
        from_date: dateRange.start,
        to_date: dateRange.end,
      });
      set({ summary: response.summary || null });
    } catch (err: any) {
      logger.error('[BudgetStore] Error loading summary:', err);
      set({ error: 'Erreur lors du chargement du résumé' });
    }
  },

  loadForecast: async () => {
    try {
      set({ forecastLoading: true });
      const data = await budgetApi.getForecast();
      set({ forecast: data });
    } catch (err: any) {
      logger.error('[BudgetStore] Error loading forecast:', err);
    } finally {
      set({ forecastLoading: false });
    }
  },

  loadRecurring: async () => {
    try {
      set({ recurringLoading: true });
      const data = await budgetApi.getRecurring();
      set({ recurringTransactions: data.recurring || [] });
    } catch (err: any) {
      logger.error('[BudgetStore] Error loading recurring:', err);
    } finally {
      set({ recurringLoading: false });
    }
  },

  setDateRange: (start, end) => {
    set({
      dateRange: { start, end },
      transactions: [],
      summary: null,
      page: 1,
      totalCount: 0,
    });
    const state = get();
    state.loadTransactions();
    state.loadBudgetLimits();
    state.loadSummary();
  },

  setMonth: (month) => {
    const range = monthBoundsForString(month);
    get().setDateRange(range.start, range.end);
  },

  setFilterType: (type) => {
    set({ filterType: type, page: 1 });
    get().loadTransactions();
  },

  setFilterTags: (tags) => {
    set({ filterTags: tags, page: 1 });
    get().loadTransactions();
  },

  toggleFilterTag: (tag) => {
    const { filterTags } = get();
    const next = filterTags.includes(tag)
      ? filterTags.filter((t) => t !== tag)
      : [...filterTags, tag];
    set({ filterTags: next, page: 1 });
    get().loadTransactions();
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query, page: 1 });
    get().loadTransactions();
  },

  setShowExcluded: (show) => {
    set({ showExcluded: show, page: 1 });
    get().loadTransactions();
  },

  setPage: (page) => {
    set({ page });
    get().loadTransactions();
  },

  setPerPage: (perPage) => {
    set({ perPage, page: 1 });
    get().loadTransactions();
  },

  refresh: async () => {
    set({ page: 1, totalCount: 0 });
    const state = get();
    set({ isLoading: true });
    await Promise.all([
      state.loadTransactions(),
      state.loadBudgetLimits(),
      state.loadSummary(),
    ]);
    set({ isLoading: false });
  },
}));
